import { useEffect, useState } from 'react';
import { TronAddress, fmtAmt, type TronWalletRow } from '@wave/shared';
import { useTronConfig, useTronWallets } from '../../api/hooks';
import {
  useCreateTronWallet,
  useDeleteTronWallet,
  useRefreshTronWallets,
  useUpdateTronConfig,
  useUpdateTronWallet,
} from '../../api/mutations';
import {
  btnDangerSm,
  btnPrimary,
  btnPrimarySm,
  btnSecondarySm,
  fieldLabelCls,
  inputCls,
  tableCell,
  tableCls,
  tableHead,
  tableHeadCell,
  tableRow,
  tableWrapper,
} from '../../lib/styles';
import { fmtAgo } from '../balances/historyMath';
import { Modal } from '../Modal';

/**
 * Управление TRON-кошельками (USDT), опрашиваемыми через TronScan и
 * подмешиваемыми отдельными строками в остатки на странице «Балансы».
 * Ключ шифруется на бэке — наружу отдаётся только факт «задан» (keySet),
 * зеркалит паттерн ChatBalancesConfigModal. Доступно admin
 * (chat-balances.manage).
 */
export function TronWalletsModal({ onClose }: { onClose: () => void }) {
  const { data: config, isLoading: configLoading } = useTronConfig();
  const { data: wallets, isLoading: walletsLoading } = useTronWallets();
  const saveConfig = useUpdateTronConfig();
  const createWallet = useCreateTronWallet();
  const deleteWallet = useDeleteTronWallet();
  const refresh = useRefreshTronWallets();

  const [enabled, setEnabled] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [configError, setConfigError] = useState<string | null>(null);
  const [configSaved, setConfigSaved] = useState(false);

  const [newAddress, setNewAddress] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  const [refreshResult, setRefreshResult] = useState<{ ok: number; failed: number } | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  useEffect(() => {
    if (config) setEnabled(config.enabled);
  }, [config]);

  async function saveConfigNow(patch: { enabled?: boolean; apiKey?: string | null }) {
    setConfigError(null);
    setConfigSaved(false);
    try {
      await saveConfig.mutateAsync(patch);
      setConfigSaved(true);
    } catch (err) {
      setConfigError((err as Error).message);
    }
  }

  async function doSaveKey() {
    const trimmed = apiKey.trim();
    if (!trimmed) return;
    await saveConfigNow({ apiKey: trimmed });
    setApiKey('');
  }

  async function doClearKey() {
    if (!confirm('Удалить ключ TronScan? Автоматический и ручной опрос остановятся до ввода нового.')) return;
    await saveConfigNow({ apiKey: null });
  }

  function toggleEnabled(next: boolean) {
    setEnabled(next);
    void saveConfigNow({ enabled: next });
  }

  async function doAdd() {
    setAddError(null);
    const address = newAddress.trim();
    const label = newLabel.trim();
    if (!label) {
      setAddError('Укажите название кошелька');
      return;
    }
    if (!TronAddress.safeParse(address).success) {
      setAddError('Неверный адрес TRON: должен начинаться с «T» и содержать 34 символа');
      return;
    }
    try {
      await createWallet.mutateAsync({ address, label });
      setNewAddress('');
      setNewLabel('');
    } catch (err) {
      setAddError((err as Error).message);
    }
  }

  async function doDelete(w: TronWalletRow) {
    if (!confirm(`Удалить кошелёк «${w.label}» (${w.address})? Действие необратимо.`)) return;
    await deleteWallet.mutateAsync(w.address);
  }

  async function doRefresh() {
    setRefreshError(null);
    setRefreshResult(null);
    try {
      const result = await refresh.mutateAsync();
      setRefreshResult(result);
    } catch (err) {
      setRefreshError((err as Error).message);
    }
  }

  const canRefresh = !!config?.enabled && !!config?.keySet;
  const isLoading = configLoading || walletsLoading;

  return (
    <Modal title="Кошельки (TronScan)" onClose={onClose} size="lg">
      {isLoading ? (
        <div className="text-sm text-text2">Загрузка…</div>
      ) : (
        <div className="space-y-5">
          <p className="text-sm text-text2">
            USDT-кошельки TRON опрашиваются через TronScan и подмешиваются в остатки на странице
            «Балансы» отдельными строками.
          </p>

          {/* ── Ключ TronScan ── */}
          <section className="space-y-3 rounded-lg border border-border bg-bg3 p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-text2">Ключ TronScan</h3>

            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className={fieldLabelCls}>API-ключ</label>
                <input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={config?.keySet ? '•••••• задан (пусто = не менять)' : 'ключ не задан'}
                  className={`${inputCls} w-full font-mono`}
                />
              </div>
              <button
                onClick={() => void doSaveKey()}
                disabled={!apiKey.trim() || saveConfig.isPending}
                className={btnSecondarySm}
              >
                Сохранить
              </button>
              <button
                onClick={() => void doClearKey()}
                disabled={!config?.keySet || saveConfig.isPending}
                className={btnDangerSm}
              >
                Очистить
              </button>
            </div>

            <label className="flex items-center gap-2 text-sm text-text">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => toggleEnabled(e.target.checked)}
                disabled={saveConfig.isPending}
              />
              Опрос включён (авто по расписанию + доступен ручной запуск)
            </label>

            {configError && <div className="text-xs text-danger">{configError}</div>}
            {configSaved && <div className="text-xs text-success">✓ сохранено</div>}
            {config?.updatedAt && (
              <div className="text-[11px] text-text-subtle">
                Изменено {fmtAgo(config.updatedAt, Date.now()) ?? config.updatedAt}
              </div>
            )}
          </section>

          {/* ── Кошельки ── */}
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-text2">Кошельки</h3>

            <div className={tableWrapper}>
              <table className={tableCls}>
                <thead>
                  <tr className={tableHead}>
                    <th className={tableHeadCell}>Название</th>
                    <th className={tableHeadCell}>Адрес</th>
                    <th className={`${tableHeadCell} text-right`}>Баланс</th>
                    <th className={tableHeadCell}>Опрос</th>
                    <th className={tableHeadCell}>Вкл</th>
                    <th className={`${tableHeadCell} text-right`}>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {(wallets ?? []).length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-3 py-8 text-center text-sm text-text2">
                        Кошельки не добавлены.
                      </td>
                    </tr>
                  )}
                  {(wallets ?? []).map((w) => (
                    <WalletRow key={w.address} wallet={w} onDelete={() => void doDelete(w)} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Форма добавления */}
            <div className="rounded-md border border-border bg-bg2 p-3">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-[2fr_1fr_auto]">
                <input
                  placeholder="Адрес TRON (T…)"
                  value={newAddress}
                  onChange={(e) => setNewAddress(e.target.value)}
                  className={`${inputCls} font-mono`}
                />
                <input
                  placeholder="Название"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  className={inputCls}
                />
                <button
                  onClick={() => void doAdd()}
                  disabled={!newAddress.trim() || !newLabel.trim() || createWallet.isPending}
                  className={btnPrimarySm}
                >
                  {createWallet.isPending ? '…' : '+ Добавить'}
                </button>
              </div>
              {addError && <div className="mt-2 text-xs text-danger">{addError}</div>}
            </div>
          </section>

          {/* ── Футер: ручной опрос ── */}
          <div className="flex items-center justify-between gap-3 border-t border-border-subtle pt-4">
            <div className="text-xs">
              {refreshResult && (
                <span className="text-text">
                  Опрошено {refreshResult.ok}, ошибок {refreshResult.failed}
                </span>
              )}
              {refreshError && <span className="text-danger">{refreshError}</span>}
              {!refreshResult && !refreshError && !canRefresh && (
                <span className="text-text2">
                  Включите опрос и задайте ключ, чтобы запустить ручной опрос.
                </span>
              )}
            </div>
            <button
              onClick={() => void doRefresh()}
              disabled={!canRefresh || refresh.isPending}
              className={btnPrimary}
            >
              {refresh.isPending ? '…' : '⟳ Обновить сейчас'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function WalletRow({ wallet, onDelete }: { wallet: TronWalletRow; onDelete: () => void }) {
  const update = useUpdateTronWallet();
  const [label, setLabel] = useState(wallet.label);
  const [labelError, setLabelError] = useState<string | null>(null);

  useEffect(() => setLabel(wallet.label), [wallet.label]);

  async function saveLabel() {
    const trimmed = label.trim();
    if (!trimmed || trimmed === wallet.label) {
      setLabel(wallet.label);
      return;
    }
    setLabelError(null);
    try {
      await update.mutateAsync({ address: wallet.address, patch: { label: trimmed } });
    } catch (err) {
      setLabelError((err as Error).message);
      setLabel(wallet.label);
    }
  }

  const ago = fmtAgo(wallet.fetchedAt, Date.now());

  return (
    <tr className={tableRow}>
      <td className={tableCell}>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={() => void saveLabel()}
          className={`${inputCls} h-8 w-full text-xs`}
        />
        {labelError && <div className="mt-1 text-[11px] text-danger">{labelError}</div>}
      </td>
      <td className={`${tableCell} font-mono text-xs`}>{wallet.address}</td>
      <td className={`${tableCell} text-right font-mono tabular-nums`}>
        {wallet.amount != null ? fmtAmt(wallet.amount, 'USDT') : <span className="text-text-subtle">—</span>}
      </td>
      <td className={tableCell}>
        {wallet.error ? (
          <span className="text-xs text-danger" title={wallet.error}>
            ошибка{ago ? ` · ${ago}` : ''}
          </span>
        ) : ago ? (
          <span className="text-xs text-text-subtle">обновлено {ago}</span>
        ) : (
          <span className="text-xs text-text-subtle">не опрошен</span>
        )}
      </td>
      <td className={tableCell}>
        <input
          type="checkbox"
          checked={wallet.enabled}
          onChange={(e) =>
            void update.mutateAsync({ address: wallet.address, patch: { enabled: e.target.checked } })
          }
        />
      </td>
      <td className={`${tableCell} text-right`}>
        <button onClick={onDelete} className={btnDangerSm}>
          Удалить
        </button>
      </td>
    </tr>
  );
}
